import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customStyles } from '../../Utils/utils';
import { Icon } from '@ohif/ui';
import { Loader } from '../../components';
import GpHeader from './GpHeader';
import {
  gpListReports,
  GpReportListItem,
  GpPagination,
} from '../../rtk-apis/gp/gpApi';
import '../../styled/pages/dashboard.scss';

const typeLabel = (t: string) => (t === 'stressEcho' ? 'Stress Echo' : 'Echo (TTE)');

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '11px',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.45)',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '13.5px',
  color: 'rgba(255,255,255,0.85)',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

const GpReportsList: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<GpReportListItem[]>([]);
  const [pagination, setPagination] = useState<GpPagination>({ total: 0, page: 1, limit: 20, pages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hoverRow, setHoverRow] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [studyType, setStudyType] = useState<'' | 'echo' | 'stressEcho'>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async (pageArg: number) => {
    setLoading(true);
    setError('');
    try {
      const result = await gpListReports({
        search: search.trim() || undefined,
        studyType: studyType || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        page: pageArg,
        limit: 20,
      });
      setReports(result.data || []);
      setPagination(result.pagination);
    } catch (err: any) {
      setError(err?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [search, studyType, dateFrom, dateTo]);

  useEffect(() => {
    load(page);
  }, [page]);

  const applyFilters = (e?: React.FormEvent) => {
    e?.preventDefault();
    setPage(1);
    load(1);
  };

  return (
    <section className="layoutContainer" style={customStyles.layoutBackground}>
      <div className="page" style={{ width: '100%' }}>
        <GpHeader />
        <div className="page__body">
          <div className="page__content">
            <div className="page__body__head">
              <div>
                <h1 className="title title--h6 title--normal" style={{ marginBottom: '2px' }}>My Reports</h1>
                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: 0 }}>
                  Finalized reports addressed to you{pagination.total > 0 ? ` — ${pagination.total} report${pagination.total === 1 ? '' : 's'}` : ''}
                </p>
              </div>
            </div>

            {/* Card container */}
            <div
              style={{
                background: 'rgba(19,27,47,0.72)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              }}
            >
              {/* Filter bar */}
              <form
                onSubmit={applyFilters}
                style={{
                  display: 'flex',
                  gap: '10px',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  padding: '14px 16px',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(255,255,255,0.025)',
                }}
              >
                <div className="relative" style={{ flex: '1 1 220px', maxWidth: '300px' }}>
                  <input
                    type="text"
                    className="form__input form__input--search"
                    placeholder="Search patient name"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ borderRadius: '8px' }}
                  />
                  {search && (
                    <span className="absolute right-8 bottom-2 cursor-pointer" onClick={() => setSearch('')}>
                      <Icon name="clear-icon" />
                    </span>
                  )}
                  <span className="absolute right-4 bottom-3 cursor-pointer">
                    <Icon name="search-icon" />
                  </span>
                </div>

                <select
                  className="form__select"
                  value={studyType}
                  onChange={e => setStudyType(e.target.value as '' | 'echo' | 'stressEcho')}
                  style={{ ...customStyles.select, width: '170px', borderRadius: '8px' }}
                >
                  <option value="">All study types</option>
                  <option value="echo">Echo (TTE)</option>
                  <option value="stressEcho">Stress Echo</option>
                </select>

                <input
                  type="date"
                  className="form__input"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  title="Study date from"
                  style={{ width: '160px', borderRadius: '8px', colorScheme: 'dark' }}
                />
                <input
                  type="date"
                  className="form__input"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  title="Study date to"
                  style={{ width: '160px', borderRadius: '8px', colorScheme: 'dark' }}
                />

                <button type="submit" className="btn btn__primary" disabled={loading} style={{ borderRadius: '8px' }}>
                  Search
                </button>
              </form>

              {error && (
                <p className="para" style={{ color: '#f87171', padding: '12px 16px', margin: 0 }}>{error}</p>
              )}

              {/* Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Patient</th>
                      <th style={thStyle}>DOB</th>
                      <th style={thStyle}>Study Type</th>
                      <th style={thStyle}>Study Date</th>
                      <th style={thStyle}>Clinic</th>
                      <th style={thStyle}>Reported By</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ padding: '48px 16px', textAlign: 'center' }}>
                          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                            No reports found
                          </div>
                          <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12.5px' }}>
                            Reports distributed to your provider number will appear here
                          </div>
                        </td>
                      </tr>
                    ) : (
                      reports.map(r => (
                        <tr
                          key={r._id}
                          style={{
                            cursor: 'pointer',
                            background: hoverRow === r._id ? 'rgba(30,169,234,0.07)' : 'transparent',
                            transition: 'background 0.15s ease',
                          }}
                          onMouseEnter={() => setHoverRow(r._id)}
                          onMouseLeave={() => setHoverRow(null)}
                          onClick={() => navigate(`/gp/reports/${r._id}`)}
                        >
                          <td style={{ ...tdStyle, color: '#fff', fontWeight: 600 }}>{r.patientName || '—'}</td>
                          <td style={tdStyle}>{r.dob || '—'}</td>
                          <td style={tdStyle}>
                            <span
                              style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                padding: '3px 10px',
                                borderRadius: '999px',
                                background: r.studyType === 'stressEcho' ? 'rgba(250,204,21,0.12)' : 'rgba(30,169,234,0.12)',
                                color: r.studyType === 'stressEcho' ? 'rgba(250,204,21,0.9)' : '#4fc3f7',
                                border: `1px solid ${r.studyType === 'stressEcho' ? 'rgba(250,204,21,0.3)' : 'rgba(30,169,234,0.3)'}`,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {typeLabel(r.studyType)}
                            </span>
                          </td>
                          <td style={tdStyle}>{r.studyDate || '—'}</td>
                          <td style={tdStyle}>{r.clinicName || '—'}</td>
                          <td style={tdStyle}>{r.finalizedBy || '—'}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <span style={{ color: '#1ea9ea', fontWeight: 600, fontSize: '13px' }}>
                              View Report →
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    padding: '12px 16px',
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginRight: 'auto' }}>
                    Page {pagination.page} of {pagination.pages} · {pagination.total} reports
                  </span>
                  <button
                    type="button"
                    className="btn btn__white btn__outlined"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage(p => p - 1)}
                    style={{ borderRadius: '8px', padding: '6px 14px', fontSize: '13px' }}
                  >
                    ← Prev
                  </button>
                  <button
                    type="button"
                    className="btn btn__white btn__outlined"
                    disabled={page >= pagination.pages || loading}
                    onClick={() => setPage(p => p + 1)}
                    style={{ borderRadius: '8px', padding: '6px 14px', fontSize: '13px' }}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Loader isLoading={loading} />
    </section>
  );
};

export default GpReportsList;
